import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../button/Button";
import Input from "../input/Input";
import styles from "../LogInForm/LogInForm.module.scss";
import { useLoginCodeRequestMutation, useLoginMethodMutation } from "../../store/services/auth-api";
import { clearCodeLogin, getApiErrorMessage, getApiErrorStatus } from "../../utils/codeLogin";

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface LogInEmailFormProps {
  initialEmail?: string;
  onPasswordMethod: (email: string) => void;
  onCodeMethod: (email: string, loginTicket: string, emailMasked: string) => void;
}

// Login step 1: the email only, then the backend says if this account uses a password or an email code
const LogInEmailForm: React.FC<LogInEmailFormProps> = ({ initialEmail = "", onPasswordMethod, onCodeMethod }) => {
  const { t } = useTranslation();
  const [emailFocused, setEmailFocused] = useState(initialEmail !== "");
  const [emailValue, setEmailValue] = useState(initialEmail);
  const [emailError, setEmailError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [loginMethod] = useLoginMethodMutation();
  const [loginCodeRequest] = useLoginCodeRequestMutation();

  const handleContinue = async () => {
    if (isLoader) return;
    const email = emailValue.trim();
    if (!EMAIL_PATTERN.test(email)) {
      setEmailError(true);
      return;
    }

    setErrorMsg("");
    setIsLoader(true);

    let method = "password";
    try {
      const payload = await loginMethod({ email }).unwrap();
      method = payload.method;
    } catch (error) {
      const status = getApiErrorStatus(error);
      if (status === 400 || status === 429) {
        setErrorMsg(getApiErrorMessage(error, t("Something went wrong. Please try again.")));
        setIsLoader(false);
        return;
      }
      // Method check not available: fall back to today's password login
      method = "password";
    }

    if (method === "code") {
      try {
        const payload = await loginCodeRequest({ email }).unwrap();
        onCodeMethod(email, payload.loginTicket, payload.emailMasked || email);
      } catch (error) {
        setErrorMsg(getApiErrorMessage(error, t("Something went wrong. Please try again.")));
        setIsLoader(false);
      }
      return;
    }

    clearCodeLogin(); // leftovers of an earlier email-code session must not apply to a password login
    onPasswordMethod(email);
  };

  return (
    <form
      className={styles.root}
      onSubmit={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
          e.preventDefault();
          handleContinue();
        }
      }}
      noValidate
    >
      <div className={styles.root_header}>
        <h1 className={styles.root_header_title}>{t("Login your account")}</h1>
        <h2 className={styles.root_header_subtitle}>
          {t("Enter the email address you used to ")}
          <span className={styles.root_header_subtitle_purchase}>{t("purchase")} </span>
          <span>{t("your Fortect subscription")}</span>
        </h2>
      </div>
      <div className={styles.root_main}>
        <div className={styles.root_main_block}>
          <Input
            value={emailValue}
            type="email"
            onFocus={() => setEmailFocused(true)}
            onBlur={() => {
              setEmailFocused(false);
              if (emailValue.trim() !== "" && !EMAIL_PATTERN.test(emailValue.trim())) {
                setEmailError(true);
              }
            }}
            onChange={(e) => {
              setEmailValue(e.target.value);
              setEmailError(false);
              setErrorMsg("");
            }}
            focused={emailFocused}
            placeholder={t("Email address")}
            error={emailError}
          />
          {emailError && (
            <p className={styles.root_errorEmail} role="alert">
              {t("Invalid email address. Please enter a valid email.")}
            </p>
          )}
        </div>
      </div>
      {errorMsg && (
        <p className={styles.root_error} role="alert">
          {errorMsg}
        </p>
      )}
      <div className={styles.root_footer}>
        <Button
          classNames={styles.root_footer_button}
          onClick={handleContinue}
          text={t("Continue")}
          isLoader={isLoader}
        />
      </div>
    </form>
  );
};

export default LogInEmailForm;
